/**
 * `<SlidingIndicator/>` — the pill that glides to the active segment of a
 * segmented control, a tab strip or a nav list (#2172, the laptop motion
 * pass; first built on the 4 Oct UI-motion branch).
 *
 * Drop it in as a child of the control's container (before the segments)
 * and give the container `relative isolate`. It measures the ONE direct
 * child marked active (`aria-pressed="true"`, `aria-selected="true"`,
 * `aria-current="page"` or `data-active="true"`) and moves there whenever
 * that changes; the segments keep their own markup and drop only their
 * active background (the pill carries it). `isolate` + the pill's
 * `z-index: -1` paint it above the container's fill and under the segment
 * labels.
 *
 * `shape="underline"` draws a 2px bar under the active tab instead of a
 * fill (tab strips).
 *
 * Zero or several active segments (a multi-select) hide the pill, so the
 * control falls back to the segments' own active styling.
 *
 * Motion: the pill's resting box is plain inline style; a move to ANOTHER
 * segment plays a FLIP on `transform` alone (`motion`'s `animate`, so it
 * runs on the compositor even while the page re-renders behind it — the
 * roster grid redrawing for a new date range). The first placement, a
 * resize, reduced motion and tests all land at once. `glide` swaps the
 * 320 ms ease-out for other timing — the phone tab bar's spring (#2172 M3).
 */
import { animate } from "motion/mini";
import { useLayoutEffect, useRef } from "react";

import { cn } from "../../lib/cn";
import {
  DUR_SLOW_MS,
  EASE_OUT_SOFT_POINTS,
  jsMotionEnabled,
  seconds,
} from "../../lib/motionTokens";

const ACTIVE_ATTRIBUTES = ["aria-pressed", "aria-selected", "aria-current", "data-active"];

/** What marks a segment active (a direct child of the container). */
export const SLIDING_ACTIVE_SELECTOR = [
  '[aria-pressed="true"]',
  '[aria-selected="true"]',
  '[aria-current="page"]',
  '[data-active="true"]',
].join(", ");

/** The container's one active direct child, or null for none / several. */
function onlyActiveChild(container: HTMLElement): HTMLElement | null {
  let found: HTMLElement | null = null;
  for (const child of Array.from(container.children)) {
    if (!(child instanceof HTMLElement) || !child.matches(SLIDING_ACTIVE_SELECTOR)) continue;
    if (found) return null;
    found = child;
  }
  return found;
}

export interface PillBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The resting transform for a box (the underline sits on its bottom edge). */
function restingTransform(box: PillBox, underline: boolean): string {
  return `translate(${box.x}px, ${underline ? box.y + box.h - 2 : box.y}px)`;
}

/**
 * The FLIP start: the pill already sized to `to`, drawn where `from` was.
 * Exported for the test.
 */
export function flipFromTransform(from: PillBox, to: PillBox, underline: boolean): string {
  const sx = to.w > 0 ? from.w / to.w : 1;
  const sy = underline || to.h <= 0 ? 1 : from.h / to.h;
  return `${restingTransform(from, underline)} scale(${sx}, ${sy})`;
}

/** How the glide plays — `motion`'s options (a spring, #2172 M3). */
export type SlidingGlide = Parameters<typeof animate>[2];

export interface SlidingIndicatorProps {
  className?: string;
  shape?: "fill" | "underline";
  /**
   * The glide's timing. Default: 320 ms easing out (D4, calm). The phone
   * tab bar passes a spring (#2172 M3: the one other overshoot D4 allows,
   * besides a sheet rising, is the tab icon's).
   */
  glide?: SlidingGlide;
  /**
   * Draw the pill this many px inside the active segment's box (#2172 M3:
   * the phone tab bar's cells fill the bar edge to edge, every px tappable,
   * while the pill sits inside the cell). Default 0.
   */
  inset?: { x: number; y: number };
  testId?: string;
}

const DEFAULT_GLIDE: SlidingGlide = { duration: seconds(DUR_SLOW_MS), ease: EASE_OUT_SOFT_POINTS };

export function SlidingIndicator({
  className,
  shape = "fill",
  glide = DEFAULT_GLIDE,
  inset,
  testId = "sliding-indicator",
}: SlidingIndicatorProps) {
  const underline = shape === "underline";
  const ref = useRef<HTMLSpanElement>(null);
  // Read when a move starts, so a caller's inline options never re-run the
  // measuring effect.
  const glideRef = useRef(glide);
  glideRef.current = glide;
  const insetX = inset?.x ?? 0;
  const insetY = inset?.y ?? 0;

  // The pill's box is written straight onto its style (no React state): a
  // move sizes the pill and starts the FLIP in the same frame, so it never
  // paints the new size at the old place, and the page around it never
  // re-renders for it.
  useLayoutEffect(() => {
    const pill = ref.current;
    const container = pill?.parentElement;
    if (!pill || !container) return;
    // The segment the pill sat on and where it was, so a move to another
    // segment can glide and a resize of the same one can snap.
    let lastActive: HTMLElement | null = null;
    let lastBox: PillBox | null = null;
    let running: { cancel: () => void } | null = null;

    const measure = () => {
      const only = onlyActiveChild(container);
      if (!only) {
        lastActive = null;
        pill.style.opacity = "0";
        return;
      }
      const c = container.getBoundingClientRect();
      const a = only.getBoundingClientRect();
      const next: PillBox = {
        x: a.left - c.left - container.clientLeft + insetX,
        y: a.top - c.top - container.clientTop + insetY,
        w: Math.max(0, a.width - 2 * insetX),
        h: Math.max(0, a.height - 2 * insetY),
      };
      const from = lastBox;
      const moved = lastActive !== null && lastActive !== only && from !== null;
      lastActive = only;
      lastBox = next;
      pill.style.width = `${next.w}px`;
      pill.style.height = `${underline ? 2 : next.h}px`;
      pill.style.transform = restingTransform(next, underline);
      pill.style.opacity = "1";
      if (!moved || !from || !jsMotionEnabled()) return;
      // `cancel`, not `stop`: stop would commit the mid-flight transform
      // over the resting box just written above.
      running?.cancel();
      running = animate(
        pill,
        {
          transform: [flipFromTransform(from, next, underline), restingTransform(next, underline)],
        },
        glideRef.current,
      );
    };

    measure();
    const mo = new MutationObserver(measure);
    mo.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ACTIVE_ATTRIBUTES,
      childList: true,
    });
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    ro?.observe(container);
    return () => {
      mo.disconnect();
      ro?.disconnect();
      running?.cancel();
    };
  }, [underline, insetX, insetY]);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      data-testid={testId}
      className={cn(
        "sliding-indicator",
        underline ? "rounded-full bg-accent" : "rounded-[inherit] bg-accent-soft",
        className,
      )}
    />
  );
}
